import { Match2RewardItem } from 'src/game/minigame/match2-timestep/components/Match2RewardItem';
import { Tooltip, TooltipOpts } from './Tooltip';
import sounds from 'src/lib/sounds';
import { Match2Reward } from 'src/replicant/match2/rewards/match2-rewards.ruleset';
import { getFlexNode } from '@play-co/timestep-core/flexbox';
import { QuickViewPool } from '@play-co/timestep-core/ui';

type RewardsTooltipOpts = TooltipOpts & {
  // Options below for for configuring the legacy Match2RewardItem components
  /**
   * Size of each reward item (width and height)
   */
  itemSize?: number;

  /**
   * Padding inside each reward item container
   */
  itemPadding?: number;

  /**
   * Margin around each reward item
   */
  itemMargin?: number;

  /**
   * Font size of the reward item label
   */
  itemLabelSize?: number;

  /**
   * Horizontal offset of the reward item label
   */
  itemLabelOffsetX?: number;

  /**
   * Vertical offset of the reward item label
   */
  itemLabelOffsetY?: number;

  /**
   * Prefix to add before each reward item label
   */
  itemLabelPrefix?: string;

  /**
   * Initial rewards to display.
   */
  rewards?: Match2Reward[];
};

type RewardsTooltipShowOpts = {
  /**
   * Override rewards to display.
   */
  rewards?: Match2Reward[];

  /**
   * Timeout in milliseconds to auto-hide the tooltip.
   */
  timeout?: number;

  /**
   * A key to identify the currently shown tooltip,
   * allowing toggling it off if the same key is used again.
   */
  key?: string;
};

/**
 * A tooltip wrapper that allows adding a preview of rewards as the content.
 */
export class RewardsTooltip extends Tooltip {
  private itemPool: QuickViewPool<Match2RewardItem>;
  private autoHideTimeout?: ReturnType<typeof setTimeout>;
  private currentKey?: string;
  private opts: RewardsTooltipOpts;

  constructor(opts: RewardsTooltipOpts) {
    super({
      ...opts,
      flex: {
        padding: 20,
        paddingBottom: opts.flex?.padding ?? 15,
        flexWrap: 'wrap',
        ...opts.flex,
      },
    });

    const itemSize = opts.itemSize ?? 60;

    this.opts = {
      itemSize,
      itemPadding: itemSize * 0.08,
      itemLabelSize: itemSize * 0.42,
      itemMargin: itemSize * 0.135,
      ...opts,
    };

    this.itemPool = new QuickViewPool<Match2RewardItem>(
      this.createRewardItem.bind(this),
    );

    // Set initial rewards, if supplied
    if (opts.rewards) {
      this.setRewards(opts.rewards);
    }
  }

  private createRewardItem() {
    const {
      itemSize,
      itemPadding = 0,
      itemMargin = 0,
      itemLabelOffsetX,
      itemLabelOffsetY,
      itemLabelSize,
      itemLabelPrefix,
    } = this.opts;
    const item = new Match2RewardItem({
      size: itemSize,
      itemPadding,
      offsetX: itemPadding * 0.5,
      offsetY: itemPadding * 0.5,
      labelOffsetX: itemLabelOffsetX,
      labelOffsetY: itemLabelOffsetY,
      labelSize: itemLabelSize,
      prefix: itemLabelPrefix,
    });
    getFlexNode(item).margin = itemMargin;
    return item;
  }

  /**
   * Show the tooltip with an optional timeout for auto-hiding.
   */
  public override async showTooltip(opts?: RewardsTooltipShowOpts) {
    const { rewards, timeout, key } = opts ?? {};

    // If currently shown tooltip has the same key, hide it instead
    if (key && this.currentKey === key) {
      sounds.sfx.play('cancel');
      return this.hideTooltip();
    }

    // Clear any existing auto hide timeout
    clearTimeout(this.autoHideTimeout);

    // Reset rewards, if supplied
    if (rewards) {
      this.setRewards(rewards);
    }

    // Auto hide after 3 seconds
    if (timeout !== undefined) {
      this.autoHideTimeout = setTimeout(() => {
        this.hideTooltip();
      }, timeout);
    }

    sounds.sfx.play('ok');
    this.currentKey = key;
    return super.showTooltip();
  }

  /**
   * Hide the tooltip, clearing any existing auto-hide timeout.
   */
  public override async hideTooltip(instant?: boolean) {
    // Clear the current key
    this.currentKey = undefined;

    // Clear any existing auto hide timeout
    clearTimeout(this.autoHideTimeout);
    this.autoHideTimeout = undefined;

    return super.hideTooltip(instant);
  }

  private setRewards(rewards: Match2Reward[]) {
    // Clear existing reward items
    this.content.removeAllSubviews();
    this.itemPool.releaseAllViews();

    // Add/reuse reward items for new rewards
    rewards.forEach((reward) => {
      const item = this.itemPool.obtainView();
      item.setReward(reward);
      this.content.addSubview(item);
    });
  }
}
